import { ChangeEvent, ReactElement, useState, useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { GQLQuery, GQLMutation, GQLOrderClassCode } from "@rx-cs-system/schema";
import { useNavigate, useLocation, Location } from "react-router";
import dayjs from "dayjs";

import { useValidation, useHandleError } from "../../../hooks";
import {
  defaultCreateDeliveryOrderFields,
  parseDeliveryOrder,
} from "../../../graphql/utils";
import { CREATE_ORDER_DELIVERY } from "../../../graphql/mutations";
import {
  CreateJsonDeliveryOrderPageProps,
  CreateJsonDeliveryOrderPage as Component,
  ValidatingForm,
} from "../../../components/pages/deliveryOrder/CreateJsonDeliveryOrderPage";
import { ProductItem } from "../../../components/atoms/ProductItem";
import {
  DeliveryTimeCode,
  DeliveryOrderFields,
  DeliveryOrderInfo,
  Products,
  OrderClassCode,
} from "../../../types";
import { GET_DELIVERY_ORDERS, GET_PRODUCTS } from "../../../graphql/queries";
import { GQLDeliveryTimeCodes } from "../../../graphql/utils";

import {
  CompleteDeliveryOrderPage,
  DeliveryOrder as CompletedDeliveryOrder,
} from "../../../components/pages/deliveryOrder/CompleteDeliveryOrderPage";

type ProductItem = {
  id: number;
  productCode: string | undefined;
  quantity: number | undefined;
};

export const CreateJsonDeliveryOrderPage = () => {
  // このページはSS権限でも許可する。
  // ※本来は広く公開したくないページ（資材の配送がこの画面を通して自由にできてしまうので）
  // ※開発工数の観点から権限を正しくセットできていない or CSシステムに一時的にある機能のため、関係者のみにURL pathを伝えている

  // @ts-ignore idが存在するのに「ない」と怒られるので
  const { handleError } = useHandleError();
  const navigate = useNavigate();
  const location: Location<DeliveryOrderInfo> = useLocation();
  const [isDisable, setIsDisable] = useState(true);
  const [isClickedSubmitButton, setIsClickedSubmitButton] = useState(false);
  const [orderJson, setOrderJson] = useState("");

  // 完了状態の管理
  const [isSubmitCompleted, setIsSubmitCompleted] = useState(false);
  const [completedDeliveryOrder, setCompletedDeliveryOrder] =
    useState<CompletedDeliveryOrder | null>(null);

  // check value is not empty, null, undefined
  const isExistValue = (value: string | undefined | null) => {
    if (value !== undefined && value !== null && value !== "") {
      return true;
    }
    return false;
  };

  // set default value for 宛名, 郵便番号, 配送先住所, 郵便番号, 電話番号
  let hasDefaultValue = false;

  const numberPattern = /[^a-zA-Z0-9]/g;
  const defaultDeliveryOrderFieldsValue = Object.assign(
    {},
    defaultCreateDeliveryOrderFields,
  );
  if (location.state) {
    // when moving from another page to this page
    // check form validation
    if (location.state.prevPageId) {
      hasDefaultValue = true;
    }
    if (isExistValue(location.state.name)) {
      defaultDeliveryOrderFieldsValue.name = location.state.name || "";
    }
    if (isExistValue(location.state.phoneNumber)) {
      defaultDeliveryOrderFieldsValue.phoneNumber =
        location.state!.phoneNumber!.replace(numberPattern, "") || "";
    }
    if (isExistValue(location.state.postalCode)) {
      defaultDeliveryOrderFieldsValue.postalCode =
        location.state!.postalCode!.replace(numberPattern, "") || "";
    }
    if (isExistValue(location.state.address)) {
      defaultDeliveryOrderFieldsValue.address = location.state.address || "";
    }
  }

  const [productItemIndex, setProductItemIndex] = useState(1);
  const [currentDeliveryOrder, setCurrentDeliveryOrder] =
    useState<DeliveryOrderFields>(defaultDeliveryOrderFieldsValue);
  // product items
  const [products, setProducts] = useState<ProductItem[]>([
    { id: productItemIndex, productCode: "", quantity: undefined },
  ]);
  // get product list
  const { data: getProducts } = useQuery<GQLQuery>(GET_PRODUCTS);

  const [createDeliveryOrder, { error: createDeliveryError }] =
    useMutation<GQLMutation>(CREATE_ORDER_DELIVERY, {
      update(cache, createDeliveryOrderData) {
        if (
          !createDeliveryOrderData ||
          !createDeliveryOrderData.data ||
          !createDeliveryOrderData.data.createDeliveryOrder
        ) {
          handleError("Mutation response undefined: createDeliveryOrder");
          return;
        }

        const deliveryOrderData: GQLQuery | null = cache.readQuery({
          query: GET_DELIVERY_ORDERS,
        });

        if (!deliveryOrderData) {
          handleError("Cache data undefined: deliveryOrderData");
          return;
        }
      },
    });
  useHandleError(createDeliveryError);

  // validation
  const {
    errors: validationErrors,
    isFormValid,
    setTouched,
    resetState,
  } = useValidation<ValidatingForm>(
    {
      name: {
        rules: {
          inputRequired: true,
          maxLength: 100,
        },
        value: currentDeliveryOrder.name,
      },
      address: {
        rules: {
          inputRequired: true,
          maxLength: 100,
        },
        value: currentDeliveryOrder.address,
      },
      postalCode: {
        rules: {
          inputRequired: true,
          isNumeric: true,
          specificLength: 7,
        },
        value: currentDeliveryOrder.postalCode,
      },
      phoneNumber: {
        rules: {
          inputRequired: true,
          isNumeric: true,
          minLength: 10,
          maxLength: 11,
        },
        value: currentDeliveryOrder.phoneNumber,
      },
      deliveryDate: {
        rules: {
          checkDateSetValidity: true,
        },
        value: currentDeliveryOrder.deliveryDate,
      },
      deliveryTimeCode: {
        rules: {},
        value: currentDeliveryOrder.deliveryTimeCode,
      },
      orderClassCode: {
        rules: {},
        value: currentDeliveryOrder.orderClassCode,
      },
    },
    [currentDeliveryOrder],
    hasDefaultValue,
  );

  // gt product list for 配送物 select box
  const productsForSelectList = getProducts
    ? getProducts.products.map((product) => {
        const item = {
          code: product.code,
          name: product.name,
          description: product.description,
        };
        return item;
      })
    : [];

  const updateDeliveryOrder = (
    item: keyof DeliveryOrderFields,
    value: string | number | undefined,
  ) => setCurrentDeliveryOrder({ ...currentDeliveryOrder, [item]: value });

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTouched("name");
    updateDeliveryOrder("name", event.target.value);
  };

  const handlePostalCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTouched("postalCode");
    updateDeliveryOrder("postalCode", event.target.value);
  };

  const handlePhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTouched("phoneNumber");
    updateDeliveryOrder("phoneNumber", event.target.value);
  };

  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTouched("address");
    updateDeliveryOrder("address", event.target.value);
  };

  const handleDeliveryDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTouched("deliveryDate");
    updateDeliveryOrder("deliveryDate", event.target.value);
  };

  const handleDeliveryTimeCodeChange = (value: string | number) => {
    setTouched("deliveryTimeCode");
    updateDeliveryOrder("deliveryTimeCode", value as DeliveryTimeCode);
  };
  const handleOrderClassCodeChange = (value: string | number) => {
    setTouched("orderClassCode");
    updateDeliveryOrder("orderClassCode", value as OrderClassCode);
  };

  const checkProductsItemValid = (items: Products[]) => {
    // not product items
    if (items.length === 0) {
      return true;
    }
    // check duplicate
    const productCodeArr = items.map((item) => item.productCode);
    const isDuplicate = productCodeArr.some((item, index) => {
      return productCodeArr.indexOf(item) != index;
    });
    // check valid
    const valid = items.some((item) => {
      return (
        item.productCode === undefined ||
        item.productCode === "" ||
        isDuplicate ||
        item.quantity === undefined
      );
    });

    return valid;
  };

  const handleProductCodeChange = (
    value: string | number,
    dataId: string | null,
  ) => {
    const productItem = [...products];
    const index = Number(dataId);
    productItem.find((x) => x.id === index)!.productCode = value.toString();
    // add productCode to product list
    setProducts(productItem);
    const productList = products.map((product) => {
      return {
        productCode: product.productCode,
        quantity: product.quantity,
      };
    });
    // set product to delivery order
    setCurrentDeliveryOrder({
      ...currentDeliveryOrder,
      ["products"]: productList,
    });
    // check validate for 配送物
    setIsDisable(checkProductsItemValid(products));
  };

  const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>) => {
    const productItem = [...products];
    const dataId = Number(event.currentTarget.dataset.id);

    // add quantity value to product list
    productItem.find((x) => x.id === dataId)!.quantity =
      Number(event.target.value) >= 1 ? Number(event.target.value) : undefined;

    setProducts(productItem);
    const productList = products.map((product) => {
      return {
        productCode: product.productCode,
        quantity: product.quantity,
      };
    });
    // set product to delivery order
    setCurrentDeliveryOrder({
      ...currentDeliveryOrder,
      ["products"]: productList,
    });
    // check validate for 配送物
    setIsDisable(checkProductsItemValid(products));
  };

  const saveJsonItems = (json: any) => {
    setCurrentDeliveryOrder({
      ...currentDeliveryOrder,
      name: json.name,
      postalCode: json.postalCode,
      phoneNumber: json.phoneNumber,
      address: json.address,
      deliveryDate: json.deliveryDate,
      deliveryTimeCode: json.deliveryTimeCode,
      orderClassCode: json.orderClassCode,
      products: json.products,
    });
    const newProducts = json.products.map(
      (
        p: {
          productCode: string;
          quantity: number;
        },
        index: number,
      ) => {
        return {
          id: index,
          productCode: p.productCode,
          quantity: p.quantity,
        };
      },
    );
    setProducts(newProducts);
    products.map((product, index) => {
      productItemsElement.push(
        <ProductItem
          key={index}
          number={index}
          products={productsForSelectList}
          productCode={product.productCode}
          quantity={product.quantity}
          onProductCodeChange={handleProductCodeChange}
          onQuantityChange={handleQuantityChange}
          onDeleteRowClick={handleDeleteProductRowClick}
        />,
      );
    });
    setIsDisable(checkProductsItemValid(newProducts));
  };

  const handleJsonFormat = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const json = JSON.parse(`${e.target.value}`);
    setOrderJson(e.target.value);
    saveJsonItems(json);
  };

  // 配送物追加
  const productItemsElement: ReactElement[] = [];

  // delete 配送物追加
  const handleDeleteProductRowClick = (dataId: number) => {
    const items: ProductItem[] = [];
    products.find((x) => {
      if (x.id !== dataId) {
        items.push(x);
      }
    });
    setProducts(items);
    const productList = items.map((item: Products) => {
      return {
        productCode: item.productCode,
        quantity: item.quantity,
      };
    });
    // set product to delivery order
    setCurrentDeliveryOrder({
      ...currentDeliveryOrder,
      ["products"]: productList,
    });
    // check validate
    setIsDisable(checkProductsItemValid(items));
  };

  // render 配送物追加
  products.forEach((product) => {
    productItemsElement.push(
      <ProductItem
        key={product.id}
        number={product.id}
        products={productsForSelectList}
        productCode={product.productCode}
        quantity={product.quantity}
        onProductCodeChange={handleProductCodeChange}
        onQuantityChange={handleQuantityChange}
        onDeleteRowClick={handleDeleteProductRowClick}
      />,
    );
  });

  // handle 中止 button click
  const handleCancelClick = async () => {
    setCurrentDeliveryOrder(defaultDeliveryOrderFieldsValue);
    setProducts([
      { id: productItemIndex, productCode: "", quantity: undefined },
    ]);
    setOrderJson("");
    resetState();
  };

  // handle 配送物追加 button click
  const handleAddProductItemClick = async () => {
    const values = [...products];
    const newProductItemIndex = productItemIndex + 1;
    setProductItemIndex(newProductItemIndex);
    values.push({
      id: newProductItemIndex,
      productCode: "",
      quantity: undefined,
    });
    setProducts(values);
    setIsDisable(true);
  };

  // handle この内容で発送 button click
  const handleSubmit = async () => {
    try {
      const deliveryOrderFields = Object.keys(currentDeliveryOrder) as Array<
        keyof typeof currentDeliveryOrder
      >;
      deliveryOrderFields.forEach((key) => setTouched(key));
      if (isFormValid) {
        setIsClickedSubmitButton(true);
        const variables = {
          input: {
            name: currentDeliveryOrder.name,
            postalCode: currentDeliveryOrder.postalCode,
            phoneNumber: currentDeliveryOrder.phoneNumber,
            address: currentDeliveryOrder.address,
            deliveryDate:
              currentDeliveryOrder.deliveryDate &&
              dayjs(currentDeliveryOrder.deliveryDate).isValid()
                ? dayjs(currentDeliveryOrder.deliveryDate).format("YYYY-MM-DD")
                : null,
            deliveryTimeCode:
              GQLDeliveryTimeCodes[currentDeliveryOrder.deliveryTimeCode],
            orderClassCode: currentDeliveryOrder.orderClassCode,
            products: currentDeliveryOrder.products,
          },
        };
        // Mutation request
        const { data } = await createDeliveryOrder({ variables });
        if (!data || !data.createDeliveryOrder) {
          handleError("Mutation response undefined: createDeliveryOrder");
          return;
        }
        // 完了状態に遷移
        handleCancelClick();
        const deliveryOrder = parseDeliveryOrder(data.createDeliveryOrder);
        setCompletedDeliveryOrder(deliveryOrder);
        setIsSubmitCompleted(true);
        setIsClickedSubmitButton(false);
      }
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    setIsDisable(isClickedSubmitButton || isDisable);
  }, [isClickedSubmitButton, isDisable]);

  // 完了画面のコールバック
  const handleCompleteDeliveryOrder = () => {
    setIsSubmitCompleted(false);
    setCompletedDeliveryOrder(null);

    const prevId = location.state?.prevPageId;
    if (!prevId) {
      return;
    }

    // orderClassCodeに応じて適切な画面に戻る
    switch (currentDeliveryOrder.orderClassCode) {
      case GQLOrderClassCode.PatientReplacementShipping:
        navigate(`/patient/${prevId}`);
        break;
      case GQLOrderClassCode.MedicalInstitutionReplacementShipping:
        navigate(`/medicalInstitution/${prevId}`);
        break;
      default:
        navigate("/deliveryOrder/create");
    }
  };

  // 完了状態の場合は完了画面を表示
  const shouldShowCompletePage = useMemo(() => {
    return isSubmitCompleted && completedDeliveryOrder;
  }, [isSubmitCompleted, completedDeliveryOrder]);

  if (shouldShowCompletePage) {
    return (
      <CompleteDeliveryOrderPage
        deliveryOrder={completedDeliveryOrder!}
        onDeliveryOrderCompleteClick={handleCompleteDeliveryOrder}
      />
    );
  }

  const props: CreateJsonDeliveryOrderPageProps = {
    orderJson,
    name: currentDeliveryOrder.name,
    postalCode: currentDeliveryOrder.postalCode,
    phoneNumber: currentDeliveryOrder.phoneNumber,
    address: currentDeliveryOrder.address,
    deliveryDate: currentDeliveryOrder.deliveryDate,
    deliveryTimeCode: currentDeliveryOrder.deliveryTimeCode,
    orderClassCode: currentDeliveryOrder.orderClassCode,
    productsItem: productItemsElement,
    validationErrors,
    isSubmitButtonDisable: isDisable,
    onNameChange: handleNameChange,
    onPostalCodeChange: handlePostalCodeChange,
    onPhoneNumberChange: handlePhoneNumberChange,
    onAddressChange: handleAddressChange,
    onDeliveryDateChange: handleDeliveryDateChange,
    onDeliveryTimeCodeChange: handleDeliveryTimeCodeChange,
    onOrderClassCodeChange: handleOrderClassCodeChange,
    onCancelButtonClick: handleCancelClick,
    onAddProductButtonClick: handleAddProductItemClick,
    onSubmitClick: handleSubmit,
    handleJsonFormat,
    isClickedSubmitButton,
  };

  return <Component {...props} />;
};
